import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import FilterSystem from '../components/filter/FilterSystem';
import FriendCard from '../components/friends/FriendCard';
import friendService from '../services/friendService';
import WaterfallLayout from '../components/common/WaterfallLayout';
import menuConfig from '../router/menuConfig';

const normalizeFilter = (filterConfig) => {
  if (!filterConfig) return filterConfig;
  if (Array.isArray(filterConfig.exprs)) {
    return {
      ...filterConfig,
      exprs: filterConfig.exprs.map((expr) =>
        expr?.field === 'topics' ? { ...expr, field: 'tags' } : expr
      ),
    };
  }
  return filterConfig;
};

const mergeFilters = (...filters) => {
  const seen = new Set();
  const exprs = [];

  filters.forEach((filter) => {
    if (!filter || !Array.isArray(filter.exprs)) return;
    filter.exprs.forEach((expr) => {
      if (!expr || !expr.field || !expr.op) return;
      const key = `${expr.field}|${expr.op}|${JSON.stringify(expr.values || [])}`;
      if (!seen.has(key)) {
        seen.add(key);
        exprs.push({ ...expr });
      }
    });
  });

  return exprs.length > 0 ? { exprs } : null;
};

const DEFAULT_BASE_FILTER = {
  exprs: [
    {
      field: 'category',
      op: 'EQ',
      values: ['post'],
    },
    {
      field: 'status',
      op: 'EQ',
      values: ['published'],
    },
  ],
};

const FindIdeasPage = () => {
    const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const observer = useRef();
  
  const location = useLocation();
  const navigate = useNavigate();

  // 优先使用菜单跳转带过来的 filterParams
  const [baseFilter, setBaseFilter] = useState(DEFAULT_BASE_FILTER);
  const [currentFilter, setCurrentFilter] = useState(() => {
    if (location.state && location.state.filterParams) {
      return normalizeFilter(location.state.filterParams);
    }
    return null;
  });
  const [currentSort, setCurrentSort] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);

  // 从URL中获取搜索参数
  const getSearchFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  };

  const [searchKeyword, setSearchKeyword] = useState(getSearchFromUrl());

  // 从localStorage中的菜单数据获取默认筛选条件
  const getDefaultFilterForPath = (path) => {
    try {
      // 优先从localStorage获取菜单数据
      const menuDataJson = localStorage.getItem('syntrust_menu_data');
      if (menuDataJson) {
        const menuData = JSON.parse(menuDataJson);
        for (const menu of menuData) {
          if (menu.children) {
            const menuItem = menu.children.find(child => child.path === path);
            if (menuItem && menuItem.defaultFilter) {
              return normalizeFilter(menuItem.defaultFilter);
            }
          }
        }
      }
      
      // 如果localStorage中没有，则从menuConfig中获取作为fallback
      for (const menu of menuConfig) {
        if (menu.children) {
          const menuItem = menu.children.find(child => child.path === path);
          if (menuItem && menuItem.defaultFilter) {
            return normalizeFilter(menuItem.defaultFilter);
          }
        }
      }
    } catch (error) {
      console.error('获取默认筛选条件失败:', error);
    }
    return null;
  };

  // 处理URL搜索参数变化
  useEffect(() => {
    const newSearchKeyword = getSearchFromUrl();
    if (newSearchKeyword !== searchKeyword) {
      setSearchKeyword(newSearchKeyword);
      setPage(1);
      setFriends([]);
      setHasMore(true);
    }
  }, [location.search, searchKeyword]);

  // 处理view_id参数变化
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view_id');
    
    // 如果没有view_id参数，说明切换到了默认菜单
    if (!viewId && location.pathname === '/explore/ideas') {
      // 从menuConfig中获取默认筛选条件
      const defaultFilter = getDefaultFilterForPath(location.pathname);
      setBaseFilter(mergeFilters(DEFAULT_BASE_FILTER, defaultFilter));
      setCurrentFilter(null);
      setCurrentSort(null);
      setCurrentGroup(null);
      setPage(1);
      setFriends([]);
      setHasMore(true);
    }
  }, [location.search, location.pathname]);

  // 处理location.state变化
  useEffect(() => {
    if (location.state) {
      // 如果是清空筛选条件的标志
      if (location.state.clearFilters) {
        setCurrentFilter(null);
        setCurrentSort(null);
        setCurrentGroup(null);
        setPage(1);
        setFriends([]);
        setHasMore(true);
      }
      // 如果是视图参数，需要同时处理多个参数
      else if (location.state.applyViewFilters) {
        if (location.state.filterParams) {
          setCurrentFilter(normalizeFilter(location.state.filterParams));
        }
        if (location.state.sortParams) {
          setCurrentSort(location.state.sortParams);
        }
        if (location.state.groupParams) {
          setCurrentGroup(location.state.groupParams);
        }
        setPage(1);
        setFriends([]);
        setHasMore(true);
      }
      // 如果只有筛选参数（默认菜单）- 只有在参数真正变化时才清空
      else if (location.state.filterParams) {
        // 检查筛选参数是否真的发生了变化
        const normalizedFilter = normalizeFilter(location.state.filterParams);
        const currentFilterStr = JSON.stringify(currentFilter);
        const newFilterStr = JSON.stringify(normalizedFilter);
        if (currentFilterStr !== newFilterStr) {
          setCurrentFilter(normalizedFilter);
          setPage(1);
          setFriends([]);
          setHasMore(true);
        }
      }
    }
  }, [location.state, currentFilter]);

  // 处理筛选变化
  const handleFilterChange = (filterParams) => {
    setCurrentFilter(filterParams ? normalizeFilter(filterParams) : null);
    setPage(1); // 重置页码
  };

  // 处理排序变化
  const handleSortChange = (sortParams) => {
    setCurrentSort(sortParams);
    setPage(1); // 重置页码
  };

  // 处理分组变化
  const handleGroupChange = (groupParams) => {
    setCurrentGroup(groupParams);
    setPage(1); // 重置页码
  };

  // 滚动加载逻辑
  const lastFriendRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new window.IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
          setLoading(true);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // 新增：创建帖子后刷新
  const handlePostCreated = () => {
    setPage(1);
    setFriends([]);
    setHasMore(true);
  };

  // 请求控制器引用
  const requestRef = useRef(null);

  // 加载数据 - 使用useCallback优化，避免不必要的重新创建
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 合并基础筛选条件、用户筛选条件与搜索条件
      const filtersToMerge = [baseFilter, currentFilter];

      if (searchKeyword) {
        filtersToMerge.push({
          exprs: [
            {
              field: 'title',
              op: 'LIKE',
              values: [searchKeyword],
            },
          ],
        });
      }

      const combinedFilter = mergeFilters(...filtersToMerge);
      
      // 构建请求参数，符合content search API的参数格式
      const requestParams = {
        type: 'article', // 内容类型为文章
        filter: combinedFilter || undefined,
        sort: currentSort || { field: 'inserted_at', desc: true }, // 默认按创建时间降序
        pagination: {
          page: page,
          per_page: 10,
        },
      };

      // 取消之前的请求
      if (requestRef.current) {
        requestRef.current.abort();
      }

      // 创建新的请求
      requestRef.current = new AbortController();
      const signal = requestRef.current.signal;

      // 调用content search API
      const response = await friendService.searchContents(requestParams, { signal });

      if (response && response.contents) {
        // 使用V2 API返回的contents数组
        setFriends((prev) =>
          page === 1 ? response.contents : [...prev, ...response.contents]
        );
        
        // 使用metadata中的分页信息判断是否还有更多数据
        if (response.metadata) {
          const { total, page: currentPage, per_page, total_pages } = response.metadata;
          setHasMore(currentPage < total_pages);
        } else {
          // 如果没有metadata，使用contents长度判断
          setHasMore(response.contents.length === 10);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('获取找队友列表失败:', error);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      requestRef.current = null;
    }
  }, [page, baseFilter, currentFilter, currentSort, searchKeyword]);

  // 使用useEffect监听fetchData的变化
  useEffect(() => {
    // 防抖处理
    const timeoutId = setTimeout(fetchData, 100);
    return () => {
      clearTimeout(timeoutId);
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchData]);

  return (
    <div className="w-[832px] flex flex-col justify-self-center items-stretch min-h-screen  mx-auto">
      <div className="mb-5">
        <FilterSystem
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onGroupChange={handleGroupChange}
          currentFilter={currentFilter}
          currentSort={currentSort}
          currentGroup={currentGroup}
          onPostCreated={handlePostCreated}
        />
      </div>



      <div className="mt-5">
        <WaterfallLayout
          items={friends.map((friend, idx) => {
            const isLast = idx === friends.length - 1;
            return {
              key: friend.id || idx,
              type: friend.fields?.cover_image ? 'friend-with-image' : 'friend-without-image',
              component: (
                <FriendCard ref={isLast ? lastFriendRef : null} {...friend} />
              ),
            };
          })}
          columns={4}
          gap="gap-4"
          heightMap={{
            'friend-with-image': '20rem',
            'friend-without-image': '10rem',
          }}
        />
      </div>

      {loading && (
        <div className="text-center py-8 text-gray-600">
          <Spin size="large" />
        </div>
      )}

      {!hasMore && friends.length > 0 && (
        <div className="text-center text-gray-400 py-4 text-sm">没有更多了</div>
      )}

      {!loading && friends.length === 0 && (
        <div className="text-center text-gray-400 py-8 text-base">
          {searchKeyword ? (
            <div>
              <div>没有找到包含 "{searchKeyword}" 的朋友帖子</div>
              <div className="text-sm mt-2">尝试使用其他关键词搜索</div>
            </div>
          ) : (
            '暂无找队友信息'
          )}
        </div>
      )}
    </div>
  );
};

export default FindIdeasPage;